import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { fetchOccurrencesByChassis } from '../api/tas';
import type { IOccurrence } from '../components/support/types';

export const OCCURRENCE_HISTORY_PAGE_SIZE = 3;
const CHASSIS_LENGTH = 17;

const isValidChassis = (chassis?: string): chassis is string => !!chassis && chassis.length === CHASSIS_LENGTH;

export interface OccurrenceHistoryState {
    items: IOccurrence[];
    nextPage: number;
    totalElements: number;
    reachedEnd: boolean;
    isLoading: boolean;
    isLoadingMore: boolean;
}

type OccurrenceHistoryAction =
    | { type: 'reset' }
    | { type: 'loadStart' }
    | { type: 'loadMoreStart' }
    | { type: 'loadMoreFailed' }
    | { type: 'pageLoaded'; page: number; content: IOccurrence[]; totalElements: number };

export const initialOccurrenceHistoryState: OccurrenceHistoryState = {
    items: [],
    nextPage: 0,
    totalElements: 0,
    reachedEnd: false,
    isLoading: false,
    isLoadingMore: false,
};

const appendUniqueById = (current: IOccurrence[], incoming: IOccurrence[]): IOccurrence[] => {
    const seen = new Set(current.map(item => item.id));
    const result = [...current];
    for (const item of incoming) {
        if (!seen.has(item.id)) {
            seen.add(item.id);
            result.push(item);
        }
    }
    return result;
};

export const occurrenceHistoryReducer = (
    state: OccurrenceHistoryState,
    action: OccurrenceHistoryAction
): OccurrenceHistoryState => {
    switch (action.type) {
        case 'reset':
            return initialOccurrenceHistoryState;
        case 'loadStart':
            return { ...initialOccurrenceHistoryState, isLoading: true };
        case 'loadMoreStart':
            return { ...state, isLoadingMore: true };
        case 'loadMoreFailed':
            return { ...state, isLoadingMore: false };
        case 'pageLoaded':
            return {
                items: appendUniqueById(action.page === 0 ? [] : state.items, action.content),
                nextPage: action.page + 1,
                totalElements: action.totalElements,
                reachedEnd: action.content.length < OCCURRENCE_HISTORY_PAGE_SIZE,
                isLoading: false,
                isLoadingMore: false,
            };
        default:
            return state;
    }
};

export const hasMoreOccurrences = (state: OccurrenceHistoryState): boolean =>
    !state.reachedEnd && state.nextPage * OCCURRENCE_HISTORY_PAGE_SIZE < state.totalElements;

export const useChassisOccurrenceHistory = (chassis?: string, currentOccurrenceId?: string | null) => {
    const [state, dispatch] = useReducer(occurrenceHistoryReducer, chassis, initialChassis => ({
        ...initialOccurrenceHistoryState,
        isLoading: isValidChassis(initialChassis),
    }));

    const generationRef = useRef(0);
    const loadingMoreRef = useRef(false);

    useEffect(() => {
        const generation = generationRef.current;
        loadingMoreRef.current = false;

        if (!isValidChassis(chassis)) {
            dispatch({ type: 'reset' });
            return () => {
                generationRef.current += 1;
            };
        }

        dispatch({ type: 'loadStart' });

        const loadFirstPage = async () => {
            try {
                const response = await fetchOccurrencesByChassis(chassis, {
                    page: 0,
                    size: OCCURRENCE_HISTORY_PAGE_SIZE,
                });
                if (generation !== generationRef.current) {
                    return;
                }
                dispatch({
                    type: 'pageLoaded',
                    page: 0,
                    content: response.content,
                    totalElements: response.totalElements,
                });
            } catch {
                if (generation === generationRef.current) {
                    dispatch({ type: 'pageLoaded', page: 0, content: [], totalElements: 0 });
                }
            }
        };

        loadFirstPage();

        return () => {
            generationRef.current += 1;
        };
    }, [chassis, currentOccurrenceId]);

    const hasMore = hasMoreOccurrences(state);

    const loadMore = useCallback(async () => {
        if (!isValidChassis(chassis) || !hasMore || state.isLoading || loadingMoreRef.current) {
            return;
        }

        loadingMoreRef.current = true;
        const generation = generationRef.current;
        const page = state.nextPage;
        dispatch({ type: 'loadMoreStart' });

        try {
            const response = await fetchOccurrencesByChassis(chassis, {
                page,
                size: OCCURRENCE_HISTORY_PAGE_SIZE,
            });
            if (generation !== generationRef.current) {
                return;
            }
            
            if (response.content.length === 0 && response.totalElements === 0) {
                dispatch({ type: 'loadMoreFailed' });
                return;
            }
            dispatch({ type: 'pageLoaded', page, content: response.content, totalElements: response.totalElements });
        } catch {
            if (generation === generationRef.current) {
                dispatch({ type: 'loadMoreFailed' });
            }
        } finally {
            if (generation === generationRef.current) {
                loadingMoreRef.current = false;
            }
        }
    }, [chassis, hasMore, state.isLoading, state.nextPage]);

    const occurrences = useMemo(
        () =>
            currentOccurrenceId
                ? state.items.filter(item => String(item.id) !== String(currentOccurrenceId))
                : state.items,
        [state.items, currentOccurrenceId]
    );

    return {
        occurrences,
        hasMore,
        isLoading: state.isLoading,
        isLoadingMore: state.isLoadingMore,
        loadMore,
    };
};
